import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { companyCodeFor } from '../../shared/trial-signup';

export const INDUSTRY_OPTIONS = ['General Service', 'Construction', 'Healthcare', 'Law Firm'];

/**
 * Sign-up step 2: company details.
 * Shows a live preview of the company code, because the server derives it
 * from the initials of the company name and single-letter codes collide.
 */
@Component({
  selector: 'app-signup-step-two',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="auth__form" [formGroup]="formGroup">
      <div class="field">
        <label for="companyName">Company name</label>
        <input
          id="companyName"
          type="text"
          class="form-control"
          formControlName="companyName"
          placeholder="Your full trading name, e.g. Makaya Legal Services"
          autocomplete="organization"
          [class.is-invalid]="isInvalid('companyName')"
        />
        <p class="field-error" *ngIf="isInvalid('companyName')">Please enter your company name.</p>
        <p class="field-hint" *ngIf="!isInvalid('companyName') && companyCode">
          Your company code will be <strong class="code-preview">{{ companyCode }}</strong>
          (the initials of your company name). You'll use it to sign in.
          <ng-container *ngIf="companyCode.length === 1">
            One-letter codes are often already taken, so consider using your full name with more than one word.
          </ng-container>
        </p>
      </div>

      <div class="field">
        <label for="industry">Industry</label>
        <select
          id="industry"
          class="form-control"
          formControlName="industry"
          [class.is-invalid]="isInvalid('industry')"
          [class.is-placeholder]="!formGroup.get('industry')?.value"
        >
          <option value="" disabled>Select your industry</option>
          <option *ngFor="let industry of industries" [value]="industry">{{ industry }}</option>
        </select>
        <p class="field-error" *ngIf="isInvalid('industry')">Please select an industry.</p>
      </div>

      <div class="field">
        <label for="numberOfEmployees">Number of employees</label>
        <input
          id="numberOfEmployees"
          type="number"
          inputmode="numeric"
          min="1"
          class="form-control"
          formControlName="numberOfEmployees"
          placeholder="e.g. 25"
          [class.is-invalid]="isInvalid('numberOfEmployees')"
        />
        <p class="field-error" *ngIf="isInvalid('numberOfEmployees')">Enter at least 1 employee.</p>
      </div>

      <div class="auth__actions">
        <button type="button" class="btn-auth btn-auth--ghost" (click)="previousStep.emit()">
          <i class="fa fa-arrow-left" aria-hidden="true"></i>
          <span>Back</span>
        </button>
        <button type="button" class="btn-auth" (click)="onNext()" [disabled]="!isStepValid()">
          <span>Continue</span>
          <i class="fa fa-arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  `
})
export class SignupStepTwoComponent {
  @Input({ required: true }) formGroup!: FormGroup;
  @Output() nextStep = new EventEmitter<void>();
  @Output() previousStep = new EventEmitter<void>();

  readonly industries = INDUSTRY_OPTIONS;
  private readonly controls = ['companyName', 'industry', 'numberOfEmployees'];

  get companyCode(): string {
    return companyCodeFor(this.formGroup.get('companyName')?.value);
  }

  isInvalid(name: string): boolean {
    const control = this.formGroup.get(name);
    return !!(control?.invalid && control.touched);
  }

  isStepValid(): boolean {
    return this.controls.every((name) => this.formGroup.get(name)?.valid);
  }

  onNext(): void {
    this.controls.forEach((name) => this.formGroup.get(name)?.markAsTouched());
    if (this.isStepValid()) {
      this.nextStep.emit();
    }
  }
}
